import { determineCompanyIcpType } from '@hellodarwin/app-core/lib/components/marketing-controlled-buttons/rules/check-company-icp';
import {
  useAnnualRevenue,
  useCompanySize,
} from '@hellodarwin/core/lib/features/helpers/company-infos';
import {
  CompanyIcpType,
  isNPO,
  OptionalRecord,
} from '@hellodarwin/core/lib/features/entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { IconNode } from '@hellodarwin/icons/dist/features/entities/general';
import Budget from '@hellodarwin/icons/dist/icons/Budget';
import Employees from '@hellodarwin/icons/dist/icons/Employees';
import Enterprise from '@hellodarwin/icons/dist/icons/Enterprise';
import Finance from '@hellodarwin/icons/dist/icons/Finance';
import Industry from '@hellodarwin/icons/dist/icons/Industry';
import Language from '@hellodarwin/icons/dist/icons/Language';
import { DefaultOptionType } from 'antd/es/select';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../app/app-hooks';
import {
  selectIndustriesICNPO,
  selectLegalStructures,
  selectNPOBeneficiaries,
  selectNPOStreams,
} from '../../../features/api/slices/grants-slice';
import { selectIndustriesSectors } from '../../../features/api/slices/tags-slice';
import { selectOnboardingData } from '../api/onboarding-slice';
import { OnboardingStep } from '../types/onboarding';
import kebabToCamelCase from '../utils/kebabToCamelCase';

export type OnboardingTemplate =
  | 'CompletionRfp'
  | 'Form'
  | 'Notifications'
  | 'RfpProject'
  | 'Welcome'
  | 'Payment'
  | 'ValidationGrants';

interface StepFormItemRules {
  required?: boolean;
  message: string;
  type?:
    | 'string'
    | 'number'
    | 'boolean'
    | 'method'
    | 'regexp'
    | 'integer'
    | 'float'
    | 'object'
    | 'enum'
    | 'date'
    | 'url'
    | 'hex'
    | 'email'
    | 'array';
  max?: number;
}
interface StepFormItemDataOrigin {
  objectKey: string;
  dataKey: string;
}

export interface StepFormItem {
  name: string;
  label: string;
  placeholder: string;
  rules?: StepFormItemRules[];
  type: 'input' | 'select' | 'checkbox' | 'radio' | 'address' | 'grid-select';
  options?: DefaultOptionType[];
  dataOrigin?: StepFormItemDataOrigin;
  multiple?: boolean;
  icon?: IconNode;
  disableTyping?: boolean;
}

export type PreviewContent = 'picture' | 'grants' | 'carousel' | 'none';
export interface StepContent {
  title?: string;
  description?: string;
  label: string;
  hideBack: boolean;
  hideNext: boolean;
  showFooter: boolean;
  showHeader: boolean;
  hideFromFlow?: boolean;
  formItems?: StepFormItem[];
  template: OnboardingTemplate;
  hideStepper?: boolean;
  isSubmitStep?: boolean;
  removeContainer?: boolean;
  previewContent?: PreviewContent;
  previewContentTablet?: PreviewContent;
}

/**
 * Steps content for the onboarding flow
 */
const useStepsContent = (steps: OnboardingStep[]) => {
  const limit = 3;

  const { t } = useTranslation();
  const stepData = useAppSelector(selectOnboardingData);

  const { selectedLocale } = useLocale();

  const industriesOptions = useAppSelector(selectIndustriesSectors);
  const annualRevenuesOptions = useAnnualRevenue();
  const companySizeOptions = useCompanySize();
  const legalStructuresOptions = useAppSelector(selectLegalStructures);
  const industriesICNPOOptions = useAppSelector((state) =>
    selectIndustriesICNPO(state, selectedLocale),
  );
  const npoBeneficiariesOptions = useAppSelector((state) =>
    selectNPOBeneficiaries(state, selectedLocale),
  );
  const npoStreamsOptions = useAppSelector((state) =>
    selectNPOStreams(state, selectedLocale),
  );
  const isICP = determineCompanyIcpType(
    selectedLocale,
    stepData.formData?.annual_revenue,
    stepData.formData?.province,
    stepData.formData?.industry_sector,
    stepData.formData?.legal_structure,
  );
  const isCompanyNPO = isNPO(stepData.formData?.legal_structure ?? '');

  return useMemo(() => {
    const stepsContent: Record<OnboardingStep, StepContent> = {
      payment: {
        title: t(
          isICP === CompanyIcpType.Custom
            ? 'onboardingClient|onboardingPaymentTitleICP'
            : 'onboardingClient|onboardingPaymentTitle',
        ),
        description: t(
          isICP === CompanyIcpType.Custom
            ? 'onboardingClient|onboardingPaymentSubtitleICP'
            : 'onboardingClient|onboardingPaymentSubtitle',
        ),
        label: t(
          `onboardingClient|${kebabToCamelCase('welcome', 'onboarding')}`,
        ),
        hideBack: false,
        hideNext: true,
        showHeader: isICP !== CompanyIcpType.Custom,
        hideStepper: true,
        showFooter: true,
        removeContainer: true,
        hideFromFlow: true,
        template: 'Payment',
        previewContent: 'none',
      },
      welcome: {
        title: t('onboardingClient|welcomeTitle'),
        description: t('onboardingClient|welcomeIntroduction'),
        label: t(
          `onboardingClient|${kebabToCamelCase('welcome', 'onboarding')}`,
        ),
        hideBack: true,
        hideNext: false,
        showHeader: false,
        showFooter: false,
        removeContainer: true,
        hideFromFlow: true,
        hideStepper: true,
        template: 'Welcome',
        previewContent: 'carousel',
        previewContentTablet: 'none',
      },
      'company-info-1': {
        title: t(`onboardingClient|onboardingObtainFundsTitle`),
        description: t(`onboardingClient|onboardingDiscoverOpportunities`),
        label: t(
          `onboardingClient|${kebabToCamelCase('company-info-1', 'onboarding')}`,
        ),
        hideBack: false,
        hideNext: false,
        showHeader: true,
        showFooter: true,
        template: 'Form',
        previewContent: 'carousel',
        previewContentTablet: 'grants',
        formItems: [
          {
            name: 'company_name',
            label: t('onboardingClient|company_name'),
            placeholder: t('profile|profileForm.company.placeholder.name'),
            rules: [
              {
                required: true,
                message: t('profile|profileForm.company.validation.name'),
              },
            ],
            type: 'input',
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'name',
            },
            icon: Enterprise,
          },
          {
            name: 'legal_structure',
            label: t('onboardingClient|legal_structure'),
            placeholder: t('onboardingClient|legal_structure'),
            rules: [
              {
                required: true,
                message: t('onboardingClient|legal_structure_required'),
              },
            ],
            type: 'select',
            options: legalStructuresOptions,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'legal_structure',
            },
            icon: Finance,
          },
          {
            name: 'city',
            label: t('onboardingClient|city'),
            placeholder: t('onboardingClient|city'),
            rules: [
              {
                required: true,
                message: t('onboardingClient|city_required'),
              },
            ],
            type: 'address',
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'city',
            },
            icon: Language,
          },
        ],
      },
      'company-details': {
        title: t(`onboardingClient|onboardingObtainFundsTitle`),
        description: t(`onboardingClient|onboardingDiscoverOpportunities`),
        label: t(
          `onboardingClient|${kebabToCamelCase('company-info-2', 'onboarding')}`,
        ),
        hideBack: false,
        hideNext: false,
        showHeader: true,
        showFooter: true,
        template: 'Form',
        previewContent: 'carousel',
        previewContentTablet: 'grants',
        formItems: [
          {
            name: 'industry_sector',
            label: t('onboardingClient|industry_sector'),
            placeholder: t(
              'profile|profileForm.company.placeholder.industrySector',
            ),
            rules: [
              {
                required: true,
                message: t('onboardingClient|industry_sector_required'),
              },
              {
                type: 'array',
                max: limit,
                message: t(
                  'profile|profileForm.company.validation.industrySectorMax3',
                ),
              },
            ],
            type: 'select',
            options: industriesOptions,
            multiple: true,
            disableTyping: true,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'industry_sector',
            },
            icon: Enterprise,
          },
          {
            name: 'annual_revenue',
            label: t('profile|profileForm.company.labels.annualRevenue'),
            placeholder: t(
              'profile|profileForm.company.placeholder.annualRevenue',
            ),
            rules: [
              {
                required: true,
                message: t(
                  'profile|profileForm.company.validation.annualRevenue',
                ),
              },
            ],
            type: 'select',
            options: annualRevenuesOptions,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'annual_revenue',
            },
            icon: Budget,
          },
          {
            name: 'company_size',
            label: t('onboardingClient|company_size'),
            placeholder: t('onboardingClient|company_size'),
            rules: [
              {
                required: true,
                message: t('onboardingClient|company_size_required'),
              },
            ],
            type: 'select',
            options: companySizeOptions,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'company_size',
            },
            icon: Employees,
          },
        ],
      },
      'company-details-npo': {
        title: t(`onboardingClient|onboardingObtainFundsTitle`),
        description: t(`onboardingClient|onboardingDiscoverOpportunities`),
        label: t(
          `onboardingClient|${kebabToCamelCase('company-info-2', 'onboarding')}`,
        ),
        hideBack: false,
        hideNext: false,
        showHeader: true,
        showFooter: true,
        template: 'Form',
        previewContent: 'carousel',
        previewContentTablet: 'grants',
        formItems: [
          {
            name: 'annual_revenue',
            label: t('profile|profileForm.company.labels.annualRevenue'),
            placeholder: t(
              'profile|profileForm.company.placeholder.annualRevenue',
            ),
            rules: [
              {
                required: true,
                message: t(
                  'profile|profileForm.company.validation.annualRevenue',
                ),
              },
            ],
            type: 'select',
            options: annualRevenuesOptions,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'annual_revenue',
            },
            icon: Budget,
          },
          {
            name: 'company_size',
            label: t('onboardingClient|company_size'),
            placeholder: t('onboardingClient|company_size'),
            rules: [
              {
                required: true,
                message: t('onboardingClient|company_size_required'),
              },
            ],
            type: 'select',
            options: companySizeOptions,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'company_size',
            },
            icon: Employees,
          },
          {
            name: 'industries_icnpo',
            label: t('onboardingClient|npoIndustries'),
            placeholder: t('onboardingClient|npoIndustries'),
            rules: [
              {
                required: true,
                message: t('onboardingClient|npoIndustries_required'),
              },
              {
                type: 'array',
                max: limit,
                message: t('onboardingClient|npoIndustries_max_limit', {
                  limit,
                }),
              },
            ],
            type: 'select',
            options: industriesICNPOOptions,
            multiple: true,
            disableTyping: true,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'industries_icnpo',
            },
            icon: Industry,
          },
          {
            name: 'npo_stream_id',
            label: t('onboardingClient|npoStreams'),
            placeholder: t('onboardingClient|npoStreams'),
            rules: [
              {
                required: true,
                message: t('onboardingClient|npoStreams_required'),
              },
            ],
            type: 'select',
            options: npoStreamsOptions,
            multiple: false,
            disableTyping: true,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'npo_stream_id',
            },
            icon: Budget,
          },
          {
            name: 'npo_beneficiaries',
            label: t('onboardingClient|npoBeneficiaries'),
            placeholder: t('onboardingClient|npoBeneficiaries'),
            rules: [
              {
                required: true,
                message: t('onboardingClient|npoBeneficiaries_required'),
              },
              {
                type: 'array',
                max: limit,
                message: t('onboardingClient|npoBeneficiaries_max_limit', {
                  limit,
                }),
              },
            ],
            type: 'select',
            options: npoBeneficiariesOptions,
            multiple: true,
            disableTyping: true,
            dataOrigin: {
              objectKey: 'company',
              dataKey: 'npo_beneficiaries',
            },
            icon: Employees,
          },
        ],
      },
    };

    // If company is NPO, remove the industry_sector field
    if (isCompanyNPO && stepsContent['company-details']) {
      stepsContent['company-details'].formItems = (
        stepsContent['company-details'].formItems ?? []
      ).filter((item) => item.name !== 'industry_sector');
    }

    const filteredSteps = steps.reduce<
      OptionalRecord<OnboardingStep, StepContent>
    >((acc, step) => {
      if (stepsContent[step]) {
        acc[step] = stepsContent[step];
      }
      return acc;
    }, {});
    return filteredSteps;
  }, [
    t,
    steps,
    annualRevenuesOptions,
    companySizeOptions,
    industriesOptions,
    legalStructuresOptions,
    industriesICNPOOptions,
    npoBeneficiariesOptions,
    npoStreamsOptions,
    isICP,
    isCompanyNPO,
  ]);
};
export default useStepsContent;
